import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AlertTriangle, Eye, CheckCircle } from "lucide-react"
import { toHref } from '@/lib/router'
import { formatBRDateTime } from "@/lib/br-dates"
import { API_BASE } from '@/lib/api-base'

interface Alert {
  id: string
  galpao_id: string | null
  lote_id: string | null
  tipo: string
  severidade: "info" | "warning" | "critical"
  mensagem: string
  status: "ativo" | "acknowledged" | "resolved"
  first_triggered_at: string
  last_triggered_at: string
  trigger_count: number
  meta: Record<string, unknown> | null
}

interface AlertListResponse {
  alerts: Alert[]
  total: number
  ativos: number
}

const SEVERITY_COLORS: Record<string, "default" | "secondary" | "destructive"> = {
  info: "secondary",
  warning: "default",
  critical: "destructive",
}

const SEVERITY_LABELS: Record<string, string> = {
  info: "Info",
  warning: "Atencao",
  critical: "Critico",
}

const STATUS_LABELS: Record<string, string> = {
  ativo: "Ativo",
  acknowledged: "Reconhecido",
  resolved: "Resolvido",
}

const STATUS_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  ativo: "destructive",
  acknowledged: "default",
  resolved: "secondary",
}

function AlertasPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [total, setTotal] = useState(0)
  const [ativos, setAtivos] = useState(0)
  const [filterStatus, setFilterStatus] = useState<"ativo" | "acknowledged" | "resolved" | "all">("ativo")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    fetchAlerts()
  }, [user, isSuperAdmin, selectedTenantId, filterStatus])

  const fetchAlerts = async () => {
    setLoading(true)
    try {
      const res = await apiFetch(`${apiBase}/api/alerts?status=${filterStatus}`)
      if (res.ok) {
        const data: AlertListResponse = await res.json()
        setAlerts(data.alerts)
        setTotal(data.total)
        setAtivos(data.ativos)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleAcknowledge = async (alertId: string) => {
    try {
      const res = await apiFetch(`${apiBase}/api/alerts/${alertId}/acknowledge`, {
        method: "POST",
      })
      if (res.ok) {
        fetchAlerts()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleResolve = async (alertId: string) => {
    try {
      const res = await apiFetch(`${apiBase}/api/alerts/${alertId}/resolve`, {
        method: "POST",
      })
      if (res.ok) {
        fetchAlerts()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const formatDate = (dateStr: string) => {
    return formatBRDateTime(dateStr)
  }

  const formatIdade = (dateStr: string) => {
    const d = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`
    return `${diffMins}m`
  }

  const getDeepLink = (alert: Alert): { url: string; label: string } | null => {
    if (!alert.meta) return null
    
    const meta = alert.meta as Record<string, unknown>
    
    if (meta.coleta_id) {
      return { url: "/classificacoes", label: "Classificar" }
    }
    if (meta.galpao_id) {
      return { url: `/galpoes`, label: "Ver Galpao" }
    }
    if (alert.tipo.includes("silo") || alert.tipo.includes("racao")) {
      return { url: "/silos", label: "Ver Silos" }
    }
    
    return null
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Alertas</h1>
          {ativos > 0 && (
            <Badge variant="destructive" className="text-sm">
              {ativos} ativo{ativos > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant={filterStatus === "ativo" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("ativo")}
        >
          Ativos
        </Button>
        <Button
          variant={filterStatus === "acknowledged" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("acknowledged")}
        >
          Reconhecidos
        </Button>
        <Button
          variant={filterStatus === "resolved" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("resolved")}
        >
          Resolvidos
        </Button>
        <Button
          variant={filterStatus === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("all")}
        >
          Todos
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-center py-8">Carregando...</p>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {filterStatus === "ativo" 
                  ? "Nenhum alerta ativo no momento." 
                  : "Nenhum alerta encontrado."}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {alerts.map((alert) => {
            const deepLink = getDeepLink(alert)
            return (
              <Card 
                key={alert.id} 
                className={
                  alert.severidade === "critical" && alert.status === "ativo"
                    ? "border-red-300 bg-red-50 dark:bg-red-950/20"
                    : alert.severidade === "warning" && alert.status === "ativo"
                      ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                      : ""
                }
              >
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant={SEVERITY_COLORS[alert.severidade] || "secondary"}>
                          {SEVERITY_LABELS[alert.severidade] || alert.severidade}
                        </Badge>
                        <Badge variant={STATUS_COLORS[alert.status] || "secondary"}>
                          {STATUS_LABELS[alert.status] || alert.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {alert.tipo}
                        </span>
                      </div>
                      
                      <p className="font-medium mb-2">{alert.mensagem}</p>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>
                          Primeira ocorrencia: {formatDate(alert.first_triggered_at)}
                        </p>
                        <p>
                          Ultima ocorrencia: {formatDate(alert.last_triggered_at)} (ha {formatIdade(alert.last_triggered_at)})
                        </p>
                        {alert.trigger_count > 1 && (
                          <p className="text-amber-600 dark:text-amber-400">
                            Ocorreu {alert.trigger_count} vezes
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {alert.status === "ativo" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledge(alert.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" /> Reconhecer
                          </Button>
                          {deepLink && (
                            <Button size="sm" asChild>
                              <Link to={toHref(deepLink.url)}>{deepLink.label}</Link>
                            </Button>
                          )}
                        </>
                      )}
                      {alert.status === "acknowledged" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleResolve(alert.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Resolver
                          </Button>
                          {deepLink && (
                            <Button size="sm" variant="outline" asChild>
                              <Link to={toHref(deepLink.url)}>{deepLink.label}</Link>
                            </Button>
                          )}
                        </>
                      )}
                      {alert.status === "resolved" && (
                        <span className="text-xs text-muted-foreground">Resolvido</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {total > alerts.length && (
        <p className="text-sm text-muted-foreground text-center mt-4">
          Mostrando {alerts.length} de {total} alertas
        </p>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/alertas')({
  component: AlertasPage,
})
